import { useEffect } from 'react';

interface UseSeoMetaOptions {
  title: string;
  description?: string;
}

/**
 * Actualiza `<title>` y `<meta name="description">` del documento en runtime.
 *
 * Caso de uso: navegación client-side. Cuando React Router cambia de ruta **sin
 * recargar la página**, el `<head>` no se actualiza solo y el tab del navegador
 * seguiría mostrando el título de la página anterior. Este hook sincroniza el
 * title/description con la ruta actual.
 *
 * Para el SEO contra crawlers (lo que lee Google en el HTML crudo), el contenido
 * correcto por ruta lo provee el prerender estático (`scripts/prerender-seo.mjs`).
 * Este hook complementa cubriendo la navegación client-side.
 *
 * Sigue el mismo patrón de `src/components/common/Canonical.tsx`
 * (querySelector + creación bajo demanda + setAttribute).
 */
const useSeoMeta = ({ title, description }: UseSeoMetaOptions): void => {
  useEffect(() => {
    document.title = title;

    if (!description) return;

    let meta = document.querySelector<HTMLMetaElement>('head > meta[name="description"]');
    if (!meta) {
      meta = document.createElement('meta');
      meta.name = 'description';
      document.head.appendChild(meta);
    }
    meta.setAttribute('content', description);
  }, [title, description]);
};

export default useSeoMeta;
